/**
 * Kit de soporte v1 — EvidenceDropzone, la zona de la captura, con el mismo
 * aspecto y comportamiento que FileUploadField de proveedores: una caja
 * punteada que abre el selector al hacer clic (o Enter/Espacio), acepta un
 * archivo arrastrado, muestra nombre y tamaño con una ✕ para quitarlo, y se
 * pone roja con un archivo inválido.
 */

import { useRef, useState } from "react";
import { clsx } from "clsx";
import { FileCheck2, Upload, X } from "lucide-react";

import { IMAGE_TYPES } from "./reportDraft";

export interface EvidenceDropzoneProps {
  value: File | null;
  onFile: (file: File | undefined) => void;
  onRemove: () => void;
  error: string | null;
  disabled?: boolean;
}

export function EvidenceDropzone({
  value,
  onFile,
  onRemove,
  error,
  disabled = false,
}: EvidenceDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const openPicker = () => {
    if (!disabled && !value) inputRef.current?.click();
  };

  return (
    <div
      role="button"
      tabIndex={disabled || value ? -1 : 0}
      aria-label="Subir captura de pantalla"
      aria-invalid={error ? true : undefined}
      onClick={openPicker}
      onKeyDown={(event) => {
        if ((event.key === "Enter" || event.key === " ") && !value) {
          event.preventDefault();
          openPicker();
        }
      }}
      onDragOver={(event) => {
        event.preventDefault();
        if (!disabled) setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(event) => {
        event.preventDefault();
        event.stopPropagation();
        setDragOver(false);
        if (!disabled) onFile(Array.from(event.dataTransfer.files)[0]);
      }}
      className={clsx(
        "rounded-xl border-2 border-dashed p-4 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand",
        disabled && "cursor-not-allowed opacity-50",
        !disabled && error && "border-err-fg bg-err-bg",
        !disabled && !error && value && "border-brand bg-card-2",
        !disabled && !error && !value && dragOver && "border-brand bg-card-2",
        !disabled &&
          !error &&
          !value &&
          !dragOver &&
          "cursor-pointer border-border-strong hover:border-brand",
      )}
    >
      <div className="flex items-center gap-3">
        <div className="shrink-0">
          {value ? (
            <FileCheck2 aria-hidden className="h-7 w-7 text-brand" />
          ) : (
            <Upload aria-hidden className="h-7 w-7 text-muted" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          {value ? (
            <>
              <div className="truncate text-sm text-fg">{value.name}</div>
              <div className="text-xs text-muted">
                {(value.size / 1024).toFixed(1)} KB
              </div>
            </>
          ) : (
            <div className="text-sm text-fg-2">
              Arrastra la imagen (PNG/JPG/WEBP) o{" "}
              <span className="font-medium text-brand">
                click para seleccionar
              </span>
            </div>
          )}
        </div>
        {value ? (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onRemove();
              if (inputRef.current) inputRef.current.value = "";
            }}
            disabled={disabled}
            aria-label="Quitar captura"
            title="Quitar archivo"
            className="px-2 text-muted hover:text-err-fg"
          >
            <X aria-hidden className="h-4 w-4" />
          </button>
        ) : null}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={IMAGE_TYPES.join(",")}
        className="hidden"
        data-testid="captura-input"
        onChange={(event) => {
          onFile(event.target.files?.[0]);
          event.target.value = "";
        }}
        disabled={disabled}
      />
    </div>
  );
}
